import { useCallback, useEffect, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';
import { CirclePlusIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { InferType } from 'yup';

import ReorderWrapper from '@/components/atoms/ReorderWrapper/ReorderWrapper';
import MediaField from '@/components/organisms/Dialogs/MediaField/MediaField';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { selectHasCarouselDialogErrors } from '@/redux/dialogAlerts/selectors';
import {
  deleteCarouselCard,
  removeCardButton,
  updateCardButtons,
  updateCarouselCard,
  updateSelectedCarouselIndex,
} from '@/redux/nodes/actions';
import { selectSelectedCarouselIndex } from '@/redux/nodes/selectors';
import { CarouselCard } from '@/shared/types/action';
import { ACCEPTED_IMAGE_FORMATS, ACCEPTED_VIDEO_FORMATS } from '@/util/constants';
import { formatErrorMessage, getCarouselPlaceholder, isCarouselPlaceholder } from '@/util/util';
import {
  CAROUSEL_SUBTITLE_MAX_LENGTH,
  CAROUSEL_SUBTITLE_META_LIMIT,
  CAROUSEL_SUBTITLE_ZENDESK_LIMIT,
} from '@/util/validation/constants';
import { carouselCardSchema } from '@/util/validation/schemas/carousel-schemas';

import CarouselButton from './CarouselButton';
import { MAX_CAROUSEL_CARD_BUTTONS, mediaOptions } from './constants';
import { move } from '../../helper';
import ToolkitAccordionWrapper from '../../ToolkitAccordionWrapper';

type Props = {
  card: CarouselCard;
  index: number;
  handleAddButton?: () => void;
  actionId: string;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  dropItem: () => void;
  updateErrors: (
    name: string,
    value: string | undefined,
    opts?: { cardIndex?: number; buttonIndex?: number }
  ) => void;
};

const CarouselAccordion = ({
  card,
  index,
  handleAddButton,
  actionId,
  moveItem,
  dropItem,
  updateErrors,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const selectedIndex = useSelector(selectSelectedCarouselIndex);
  const blockReordering = useSelector(selectHasCarouselDialogErrors(actionId));

  const isExpanded = selectedIndex === index;

  const { title, subtitle, media, buttons } = card;
  const hasMaxButtons = buttons?.length === MAX_CAROUSEL_CARD_BUTTONS;

  const formMethods = useForm<InferType<typeof carouselCardSchema>>({
    mode: 'onChange',
    defaultValues: { title, subtitle: subtitle ?? '', media },
    resolver: yupResolver<
      InferType<typeof carouselCardSchema>,
      unknown,
      InferType<typeof carouselCardSchema>
    >(carouselCardSchema),
  });
  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  useEffect(() => {
    if (title !== getValues('title')) {
      setValue('title', title);
    }
    if ((subtitle ?? '') !== getValues('subtitle')) {
      setValue('subtitle', subtitle ?? '');
    }
    if (media.url !== getValues('media.url')) {
      setValue('media.url', media.url);
    }
  }, [title, subtitle, media.url, getValues, setValue]);

  const updateRedux = useCallback(
    (key: string, value: string) => {
      const updated = cloneDeep(card);
      set(updated, key, value);
      dispatch(updateCarouselCard({ actionId, index, action: updated }));
    },
    [dispatch, actionId, index, card]
  );

  const handleDelete = useCallback(() => {
    updateErrors('title', undefined, { cardIndex: index });
    updateErrors('subtitle', undefined, { cardIndex: index });
    updateErrors('url', undefined, { cardIndex: index });
    buttons.forEach((_, i) => {
      updateErrors('label', undefined, { cardIndex: index, buttonIndex: i });
      updateErrors('buttonUrl', undefined, { cardIndex: index, buttonIndex: i });
      updateErrors('value', undefined, { cardIndex: index, buttonIndex: i });
    });
    dispatch(deleteCarouselCard({ currentIndex: index, actionId }));
  }, [buttons, index, actionId, dispatch, updateErrors]);

  const handleChange = () => {
    dispatch(updateSelectedCarouselIndex({ index: isExpanded ? null : index }));
  };

  const handleDeleteButton = (buttonIndex: number) => {
    dispatch(removeCardButton({ actionId, cardIndex: index, buttonIndex }));
  };

  const handleDragButton = (dragIndex: number, hoverIndex: number) => {
    const reordered = move(cloneDeep(buttons), dragIndex, hoverIndex);
    dispatch(updateCardButtons({ actionId, cardIndex: index, buttons: reordered }));
  };

  useEffect(() => {
    trigger(); // run once
  }, [trigger]);

  // Track previous error messages to prevent unnecessary dispatches
  const prevErrorsRef = useRef<{ title?: string; subtitle?: string; url?: string }>({});

  useEffect(() => {
    const titleMsg = formatErrorMessage(errors.title);
    const subtitleMsg = formatErrorMessage(errors.subtitle);
    const urlMsg = formatErrorMessage(errors.media?.url);

    const prevErrors = prevErrorsRef.current;

    const cardOpts = { cardIndex: index };

    // Only dispatch if errors have actually changed
    if (titleMsg !== prevErrors.title) {
      updateErrors('title', titleMsg || undefined, cardOpts);
      prevErrors.title = titleMsg;
    }
    if (subtitleMsg !== prevErrors.subtitle) {
      updateErrors('subtitle', subtitleMsg || undefined, cardOpts);
      prevErrors.subtitle = subtitleMsg;
    }
    if (urlMsg !== prevErrors.url) {
      updateErrors('url', urlMsg || undefined, cardOpts);
      prevErrors.url = urlMsg;
    }
  }, [
    errors.title,
    errors.title?.message,
    errors.subtitle,
    errors.subtitle?.message,
    errors.media?.url,
    errors.media?.url?.message,
    index,
    updateErrors,
  ]);

  return (
    <ReorderWrapper
      dragId="carousel-card"
      index={index}
      placeholderHeight={44}
      moveItem={moveItem}
      dropItem={dropItem}
      getIndex={() => index}
      canDrag={!isExpanded && !blockReordering}
    >
      <ToolkitAccordionWrapper
        onChange={handleChange}
        expanded={isExpanded}
        title={title}
        handleDelete={handleDelete}
      >
        <Form {...formMethods}>
          <div className="mb-6">
            <Label className="mb-2 block">{t('dialog.carousel.media_type')}</Label>
            <FormField
              control={control}
              name="media.type"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    updateRedux('media.type', value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mediaOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.label_key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="mb-4">
            <MediaField
              name="media.url"
              error={!!errors.media?.url}
              errorMessage={errors.media?.url?.message}
              setValue={setValue}
              urlValue={isCarouselPlaceholder(media.url) ? '' : media.url}
              accept={media.type === 'image' ? ACCEPTED_IMAGE_FORMATS : ACCEPTED_VIDEO_FORMATS}
              shouldCrop={media.type === 'image'}
              aspectRatio="horizontal"
              onFileRemoved={() => {
                const type = getValues('media.type');
                updateRedux('media.url', getCarouselPlaceholder({ type, id: index + 1 }));
              }}
              onChange={() => updateRedux('media.url', getValues('media.url'))}
            />
          </div>

          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem className="mb-6">
                <FormLabel>{t('common.title')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder=""
                    onChange={(event) => {
                      field.onChange(event);
                      updateRedux('title', event.target.value);
                    }}
                  />
                </FormControl>
                <FormMessage>{errors.title?.message}</FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="subtitle"
            render={({ field }) => {
              const subtitleLength = field.value?.length ?? 0;
              return (
                <FormItem className="mb-6">
                  <FormLabel>{t('common.description')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={CAROUSEL_SUBTITLE_MAX_LENGTH}
                      onChange={(event) => {
                        field.onChange(event);
                        updateRedux('subtitle', event.target.value || undefined);
                      }}
                    />
                  </FormControl>
                  {subtitleLength > CAROUSEL_SUBTITLE_META_LIMIT && (
                    <FormDescription className="text-warning">
                      {t('dialog.carousel.subtitle_cut_meta', { 0: CAROUSEL_SUBTITLE_META_LIMIT })}
                      {subtitleLength > CAROUSEL_SUBTITLE_ZENDESK_LIMIT &&
                        ` ${t('dialog.carousel.subtitle_cut_zendesk', { 0: CAROUSEL_SUBTITLE_ZENDESK_LIMIT })}`}
                    </FormDescription>
                  )}
                  <FormMessage>{errors.subtitle?.message}</FormMessage>
                </FormItem>
              );
            }}
          />
        </Form>

        <div className="flex flex-col justify-center gap-2">
          {buttons.map((button, i) => (
            <CarouselButton
              key={button.id}
              index={i}
              option={button}
              actionId={actionId}
              cardIndex={index}
              disabled={buttons.length === 1}
              handleDeleteButton={handleDeleteButton}
              moveItem={handleDragButton}
              dropItem={() => {}}
              getIndex={() => i}
              updateErrors={updateErrors}
            />
          ))}
        </div>

        <div className="pt-2 text-right">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleAddButton}
            disabled={hasMaxButtons}
            className="[&_svg]:text-foreground-muted"
          >
            <CirclePlusIcon size={16} />
            {t('common.add_button')}
          </Button>
        </div>
      </ToolkitAccordionWrapper>
    </ReorderWrapper>
  );
};

export default CarouselAccordion;
